import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  LogOut,
  Plus,
  Settings,
  Terminal,
} from "lucide-react";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useDefaultWorkspace } from "@/hooks/useDefaultWorkspace";
import { useDepartmentStore } from "@/features/department";
import { jwtLogout } from "@/lib/jwt-auth-client";
import { useIsAdmin } from "@/shared/hooks/useIsAdmin";
import { cn } from "@/lib/utils";

/**
 * Org / workspace switcher + sign-out menu.
 *
 * Lifted verbatim from the old `MiniContextSelector` so the org switch,
 * workspace switch, new-org link, new-workspace link, and sign-out flows keep
 * their existing behavior. Only the trigger styling and popover anchoring
 * were adjusted to fit the sidebar bottom rail (popover anchors to the right
 * of the trigger so it does not get clipped at the bottom of the viewport).
 *
 * Mounted at the bottom of the expanded `AppSidebar` panel.
 */
export function AppSidebarUserMenu() {
  const { organizations, activeOrganizationId, switchOrganization, fetchOrganizations, loaded } =
    useOrganizationStore();
  const { workspaces, refresh: refreshWorkspaces } = useWorkspaces();
  const { defaultWorkspaceId, setDefaultWorkspace } = useDefaultWorkspace();
  const { isStaff } = useIsAdmin();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loaded) {
      void fetchOrganizations();
    }
  }, [loaded, fetchOrganizations]);

  // Re-fetch workspace list when default switches to one not in our list (e.g. just created)
  useEffect(() => {
    if (defaultWorkspaceId && !workspaces.some((w) => w.id === defaultWorkspaceId)) {
      refreshWorkspaces();
    }
  }, [defaultWorkspaceId, workspaces, refreshWorkspaces]);

  // Re-fetch workspaces every time the dropdown opens so newly created workspaces appear
  useEffect(() => {
    if (open) {
      refreshWorkspaces();
    }
  }, [open, refreshWorkspaces]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  async function handleSignOut() {
    setOpen(false);
    useDepartmentStore.getState().reset();
    jwtLogout();
    navigate("/");
    window.location.reload();
  }

  const activeOrg = organizations.find((w) => w.id === activeOrganizationId);
  const activeWorkspace = workspaces.find((w) => w.id === defaultWorkspaceId) || workspaces[0];

  if (!activeOrg) {
    return (
      <div className="ds-micro px-3 py-2 text-foreground/50 truncate">Organization</div>
    );
  }

  // Display format: "OrgName / WorkspaceName" if a workspace exists, else just "OrgName"
  const displayText = activeWorkspace
    ? `${activeOrg.name} / ${activeWorkspace.name}`
    : activeOrg.name;

  return (
    <div ref={ref} className="relative w-full">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 w-full px-3 py-2 rounded-xl transition-colors",
          "ds-caption text-foreground/70 hover:text-foreground hover:bg-accent/50",
          open && "bg-accent/40 text-foreground",
        )}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-foreground/10 text-foreground/80">
          <Building2 className="h-3.5 w-3.5" />
        </span>
        <span className="flex-1 truncate text-left">{displayText}</span>
        {open ? (
          <ChevronUp className="h-3.5 w-3.5 shrink-0 text-foreground/40" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-foreground/40" />
        )}
      </button>

      {open && (
        <div className="absolute left-full bottom-0 ml-2 w-60 z-[70] rounded-xl bg-popover/95 backdrop-blur-[16px] shadow-ambient py-1 border border-border/50 max-h-[460px] overflow-y-auto ds-scrollbar flex flex-col">
          {/* Organizations Section */}
          <div className="ds-micro-strong px-2 py-1.5 text-muted-foreground uppercase">
            Organization
          </div>
          {organizations.map((w) => (
            <div key={w.id} className="flex items-center group">
              <button
                onClick={() => {
                  switchOrganization(w.id);
                  setOpen(false);
                }}
                className={cn(
                  "ds-caption flex-1 flex items-center gap-2 px-3 py-1.5 transition-colors",
                  w.id === activeOrganizationId
                    ? "text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted",
                )}
              >
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">{w.name}</span>
                {w.id === activeOrganizationId && (
                  <Check className="h-3 w-3 ml-auto text-primary" />
                )}
              </button>
              <Link
                to={`/settings/organization`}
                onClick={() => setOpen(false)}
                className="px-2 text-muted-foreground hover:text-foreground transition-all"
                title="Organization settings"
              >
                <Settings className="h-3 w-3" />
              </Link>
            </div>
          ))}
          <div className="pt-1 pb-2">
            <Link
              to="/onboarding/create-organization?create=true"
              onClick={() => setOpen(false)}
              className="ds-caption flex items-center gap-2 px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Plus className="h-3 w-3" />
              New organization
            </Link>
          </div>

          {/* Workspaces Section */}
          {workspaces.length > 0 && (
            <>
              <div className="border-t border-border/50 my-1"></div>
              <div className="ds-micro-strong px-2 py-1.5 text-muted-foreground uppercase">
                Workspace
              </div>
              {workspaces.map((ws) => (
                <div key={ws.id} className="flex items-center group">
                  <button
                    onClick={() => {
                      setDefaultWorkspace({ id: ws.id, slug: ws.slug });
                      setOpen(false);
                      navigate(`/workspaces`);
                    }}
                    className={cn(
                      "ds-caption flex-1 flex items-center gap-2 px-3 py-1.5 transition-colors",
                      (
                        defaultWorkspaceId
                          ? ws.id === defaultWorkspaceId
                          : ws.id === activeWorkspace?.id
                      )
                        ? "text-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted",
                    )}
                  >
                    <LayoutDashboard className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{ws.name}</span>
                    {(defaultWorkspaceId
                      ? ws.id === defaultWorkspaceId
                      : ws.id === activeWorkspace?.id) && (
                      <Check className="h-3 w-3 ml-auto text-primary" />
                    )}
                  </button>
                  <Link
                    to={`/workspaces/${ws.slug || ws.id}`}
                    onClick={() => setOpen(false)}
                    className="px-2 text-muted-foreground hover:text-foreground transition-all"
                    title="Workspace settings"
                  >
                    <Settings className="h-3 w-3" />
                  </Link>
                </div>
              ))}
            </>
          )}
          <div className={workspaces.length === 0 ? "border-t border-border/50 pt-1 mt-1" : "pt-1"}>
            <Link
              to="/workspaces?new=true"
              onClick={() => setOpen(false)}
              className="ds-caption flex items-center gap-2 px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Plus className="h-3 w-3" />
              New workspace
            </Link>
          </div>

          {isStaff && (
            <div className="border-t border-border/50 mt-1 pt-1">
              <Link
                to="/console"
                onClick={() => setOpen(false)}
                className="ds-caption flex items-center gap-2 px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Terminal className="h-3.5 w-3.5" />
                Agent console
                <span className="ds-micro ml-auto rounded bg-amber-500/15 px-1.5 py-0.5 uppercase text-amber-700 dark:text-amber-400">
                  staff
                </span>
              </Link>
            </div>
          )}

          <div className="mt-auto pt-2 border-t border-border/50">
            <button
              onClick={handleSignOut}
              className="ds-caption flex w-full items-center gap-2 px-3 py-2 text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
